import { StopAddressType } from "types/MultiStop";

const STOP_REGULAR_ADDRESS_FIELDS = [
  "address_id",
  "address_name",
  "address_reference_number",
  "street_address",
  "street_address_line_two",
  "zipcode",
  "city_name",
  "city_id",
  "state_name",
  "state_id",
  "country_name",
  "country_id",
  "location_reference_number",
  "custom_address_reference",
  "un_locode",
  "port_country",
] as const;

const STOP_AIRPORT_FIELDS = ["stop_airport_name", "stop_airport_code"] as const;

const STOP_SEAPORT_FIELDS = ["stop_seaport_name", "stop_seaport_code"] as const;

const PARTY_REGULAR_ADDRESS_FIELDS = [
  "address_id",
  "address_reference_number",
  "address_name",
  "address_street",
  "address_street_line2",
  "address_city",
  "address_state",
  "address_country",
  "address_zipcode",
  "address_un_locode",
] as const;

const PARTY_AIRPORT_FIELDS = ["airport", "airport_name"] as const;

const PARTY_SEAPORT_FIELDS = ["seaport", "seaport_name"] as const;

const PARTY_ADDRESS_FIELDS = [
  ...PARTY_REGULAR_ADDRESS_FIELDS,
  ...PARTY_AIRPORT_FIELDS,
  ...PARTY_SEAPORT_FIELDS,
] as const;

function deleteFields(obj: Record<string, unknown>, fields: readonly string[]) {
  fields.forEach((field) => {
    delete obj[field];
  });
}

function isEmptyValue(value: unknown): boolean {
  return value === null || value === undefined || value === "";
}

function removeEmptyKeys(obj: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(Object.entries(obj).filter(([, value]) => !isEmptyValue(value)));
}

function hasSelectedRegularStopAddress(stop: Record<string, unknown>): boolean {
  return Boolean(
    stop.address_id || stop.address_name || stop.street_address || stop.address_reference_number,
  );
}

/** Book address explicitly chosen on an airport/seaport stop (not orphan street text). */
function hasExplicitStopAddressSelection(stop: Record<string, unknown>): boolean {
  return Boolean(stop.address_id || stop.address_reference_number || stop.address_name);
}

function hasSelectedStopAddress(stop: Record<string, unknown>): boolean {
  const addressType = stop.stop_address_type as StopAddressType | undefined;

  if (addressType === StopAddressType.AIRPORT) {
    return Boolean(stop.stop_airport_code);
  }

  if (addressType === StopAddressType.SEAPORT) {
    return Boolean(stop.stop_seaport_code);
  }

  return hasSelectedRegularStopAddress(stop);
}

function hasSelectedRegularPartyAddress(party: Record<string, unknown>): boolean {
  return Boolean(
    party.address_id || party.address_reference_number || party.address_name || party.address_street,
  );
}

/** Book address explicitly chosen on an airport/seaport party (not orphan street text). */
function hasExplicitPartyAddressSelection(party: Record<string, unknown>): boolean {
  return Boolean(party.address_id || party.address_reference_number || party.address_name);
}

function hasSelectedPartyAddress(party: Record<string, unknown>): boolean {
  const addressType = party.address_type;

  if (addressType === "airport") {
    return Boolean(party.airport);
  }

  if (addressType === "seaport") {
    return Boolean(party.seaport);
  }

  return hasSelectedRegularPartyAddress(party);
}

function stripStopAddressFields(stop: Record<string, unknown>): Record<string, unknown> {
  const result = { ...stop };
  const addressType = stop.stop_address_type as StopAddressType | undefined;

  if (addressType === StopAddressType.AIRPORT) {
    if (!stop.stop_airport_code) {
      deleteFields(result, STOP_AIRPORT_FIELDS);
    }
    if (!hasExplicitStopAddressSelection(stop)) {
      deleteFields(result, STOP_REGULAR_ADDRESS_FIELDS);
    }
    deleteFields(result, STOP_SEAPORT_FIELDS);
    return result;
  }

  if (addressType === StopAddressType.SEAPORT) {
    if (!stop.stop_seaport_code) {
      deleteFields(result, STOP_SEAPORT_FIELDS);
    }
    if (!hasExplicitStopAddressSelection(stop)) {
      deleteFields(result, STOP_REGULAR_ADDRESS_FIELDS);
    }
    deleteFields(result, STOP_AIRPORT_FIELDS);
    return result;
  }

  if (!hasSelectedRegularStopAddress(stop)) {
    deleteFields(result, STOP_REGULAR_ADDRESS_FIELDS);
  }
  deleteFields(result, STOP_AIRPORT_FIELDS);
  deleteFields(result, STOP_SEAPORT_FIELDS);

  return result;
}

function stripPartyAddressFields(party: Record<string, unknown>): Record<string, unknown> {
  const result = { ...party };
  const addressType = party.address_type;

  if (!hasSelectedPartyAddress(party)) {
    deleteFields(result, PARTY_ADDRESS_FIELDS);
    return result;
  }

  if (addressType === "airport") {
    if (!hasExplicitPartyAddressSelection(party)) {
      deleteFields(result, PARTY_REGULAR_ADDRESS_FIELDS);
    }
    deleteFields(result, PARTY_SEAPORT_FIELDS);
    return result;
  }

  if (addressType === "seaport") {
    if (!hasExplicitPartyAddressSelection(party)) {
      deleteFields(result, PARTY_REGULAR_ADDRESS_FIELDS);
    }
    deleteFields(result, PARTY_AIRPORT_FIELDS);
    return result;
  }

  deleteFields(result, PARTY_AIRPORT_FIELDS);
  deleteFields(result, PARTY_SEAPORT_FIELDS);
  return result;
}

function hasConsigneeData(consignee: Record<string, unknown>): boolean {
  return Boolean(
    consignee.same_as_delivery_address_contact ||
      consignee.contact_name ||
      consignee.contact_phone ||
      consignee.contact_email ||
      consignee.address_street ||
      consignee.address_reference_number ||
      consignee.airport ||
      consignee.airport_name ||
      consignee.seaport ||
      consignee.seaport_name,
  );
}

function hasNotifyPartyData(notifyParty: Record<string, unknown>): boolean {
  const sameAs = notifyParty.same_as_address_contact ?? "customAddressContact";

  return Boolean(
    sameAs !== "customAddressContact" ||
      notifyParty.contact_name ||
      notifyParty.contact_phone ||
      notifyParty.contact_email ||
      notifyParty.address_street ||
      notifyParty.address_reference_number ||
      notifyParty.airport ||
      notifyParty.airport_name ||
      notifyParty.seaport ||
      notifyParty.seaport_name,
  );
}

function stripScheduleParties(schedule: Record<string, unknown>): Record<string, unknown> {
  const result = { ...schedule };

  const { consignee } = schedule;
  if (consignee && typeof consignee === "object") {
    const strippedConsignee = removeEmptyKeys(stripPartyAddressFields(consignee as Record<string, unknown>));

    if (hasConsigneeData(strippedConsignee)) {
      result.consignee = strippedConsignee;
    } else {
      delete result.consignee;
    }
  }

  const notifyParty = schedule.notify_party;
  if (notifyParty && typeof notifyParty === "object") {
    const strippedNotifyParty = removeEmptyKeys(stripPartyAddressFields(notifyParty as Record<string, unknown>));

    if (hasNotifyPartyData(strippedNotifyParty)) {
      result.notify_party = strippedNotifyParty;
    } else {
      delete result.notify_party;
    }
  }

  return result;
}

export function stripUnselectedScheduleAddresses(payload: Record<string, unknown>): Record<string, unknown> {
  const { schedule } = payload;

  if (!schedule || typeof schedule !== "object") {
    return payload;
  }

  const scheduleRecord = schedule as Record<string, unknown>;
  const { stops } = scheduleRecord;

  const strippedStops = Array.isArray(stops)
    ? stops
        .filter((stop) => stop && typeof stop === "object" && hasSelectedStopAddress(stop as Record<string, unknown>))
        .map((stop) => stripStopAddressFields(stop as Record<string, unknown>))
    : stops;

  return {
    ...payload,
    schedule: stripScheduleParties({
      ...scheduleRecord,
      ...(strippedStops !== undefined ? { stops: strippedStops } : {}),
    }),
  };
}
